// useInstallPrompt — Progressive install prompt state machine
//
// Listens for the non-standard `beforeinstallprompt` event, captures it,
// and exposes an imperative API the InstallPromptBanner uses to offer the
// install at the right moment. Tracks dismissal and visit count in
// localStorage so the xs-sm "after 3rd visit" heuristic from
// RESPONSIVE_DESIGN.md §20.2 can be satisfied client-side.

import { useCallback, useEffect, useState } from 'react';
import type { BeforeInstallPromptEvent } from '@bbpos/types';

const LS_DISMISSED_AT = 'bbpos_install_dismissed_at';
const LS_INSTALLED = 'bbpos_install_completed';
const LS_VISIT_COUNT = 'bbpos_install_visit_count';

/** Time (ms) to suppress the prompt after the user dismisses it. 7 days. */
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

function readNumber(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function writeNumber(key: string, value: number) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // localStorage can throw in private-mode iframes — suppress.
  }
}

function writeString(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // no-op
  }
}

function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari exposes navigator.standalone instead.
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

export interface UseInstallPromptReturn {
  /** The deferred event is available and the user hasn't already installed / dismissed. */
  canInstall: boolean;
  /** Number of times the user has opened this app. Persisted across sessions. */
  visitCount: number;
  /** True once the user is running the app in standalone (installed) mode. */
  isStandalone: boolean;
  /** Trigger the browser install prompt. Resolves after the user decides. */
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
  /** Hide the prompt for DISMISS_COOLDOWN_MS. */
  dismiss: () => void;
}

export function useInstallPrompt(): UseInstallPromptReturn {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LS_INSTALLED) === '1';
    } catch {
      return false;
    }
  });
  const [dismissedAt, setDismissedAt] = useState<number>(() => readNumber(LS_DISMISSED_AT, 0));
  const [visitCount, setVisitCount] = useState<number>(() => readNumber(LS_VISIT_COUNT, 0));
  const [isStandalone, setIsStandalone] = useState<boolean>(() => isStandaloneMode());

  // Increment visit count exactly once per mount.
  useEffect(() => {
    const next = readNumber(LS_VISIT_COUNT, 0) + 1;
    writeNumber(LS_VISIT_COUNT, next);
    setVisitCount(next);
  }, []);

  // Capture the deferred install event.
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler as EventListener);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler as EventListener);
    };
  }, []);

  // Detect post-install success so the banner goes away immediately.
  useEffect(() => {
    const handler = () => {
      writeString(LS_INSTALLED, '1');
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('appinstalled', handler);
    return () => {
      window.removeEventListener('appinstalled', handler);
    };
  }, []);

  // React to display-mode changes (e.g. user installs + returns).
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(display-mode: standalone)');
    const handler = (e: MediaQueryListEvent) => setIsStandalone(e.matches);
    mql.addEventListener?.('change', handler);
    return () => mql.removeEventListener?.('change', handler);
  }, []);

  const install = useCallback(async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    if (!deferred) return 'unavailable';
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      if (choice.outcome === 'accepted') {
        writeString(LS_INSTALLED, '1');
        setInstalled(true);
      }
      return choice.outcome;
    } catch {
      setDeferred(null);
      return 'dismissed';
    }
  }, [deferred]);

  const dismiss = useCallback(() => {
    const now = Date.now();
    writeNumber(LS_DISMISSED_AT, now);
    setDismissedAt(now);
  }, []);

  const recentlyDismissed = dismissedAt > 0 && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS;
  const canInstall = !installed && !isStandalone && !recentlyDismissed && deferred !== null;

  return { canInstall, visitCount, isStandalone, install, dismiss };
}
